import type { ReactNode } from "react";
import type { Block, ContentResource, UnitStandard } from "../content/model";

// THE COMPONENT REGISTRY: what a spec is allowed to say, and when each thing is the right answer.
//
// Every entry declares its props, the data shapes it accepts, and one or two sentences about WHEN
// TO USE IT. Those sentences are written for a model to read, because one will: the generated
// producer's whole job is choosing from this list, and a component described as "renders a list"
// gives it nothing to choose on. Describe the situation, not the markup.
//
// MID-LEVEL ON PURPOSE. Not primitives — a registry of `div` and `span` is HTML with extra steps,
// and it would push layout decisions into the spec, where they cannot be tested. Not
// god-components either: one `UnitPage` component would make the spec a single node and the whole
// arrangement pointless. The test is whether a component corresponds to something a teacher would
// name: the standards, the prompt, the handout, the skill.
//
// THE MARKUP IS THE MARKUP THAT WAS ALREADY THERE. Same class names as PlanView used before this
// existed, so the refactor moves no pixels. Anything else would make it impossible to tell a
// rendering bug from a restyle.
//
// Bump REGISTRY_VERSION when a component is added, removed, or its props change. It is half the
// cache key: a spec produced against an older registry may name something that no longer exists.

export const REGISTRY_VERSION = 1;

export type PropType = "text" | "prose" | "number" | "boolean" | "text_list" | "standards"
                     | "blocks" | "resources";

export interface PropSpec {
  type: PropType;
  required?: boolean;
  /** What this prop is for. Read by the validator's error messages and by whoever writes a spec. */
  semantic: string;
}

export interface ComponentSpec {
  name: string;
  /** One or two sentences: the SITUATION this component is the right answer to. */
  whenToUse: string;
  /** The data shapes it accepts, in words — what a binding pointed at it should resolve to. */
  accepts: string;
  props: Record<string, PropSpec>;
  /** May a spec give this component children? */
  children?: boolean;
  render: (props: Record<string, unknown>, children: ReactNode) => ReactNode;
}

// ---- helpers, not registry entries ------------------------------------------------------------
// Deliberately private. `Field` is a label and a value — a layout atom, not something a teacher
// would name — and registering it would invite specs to build screens out of dt/dd pairs.

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="pv-field">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

const str = (v: unknown): string => (v === null || v === undefined ? "" : String(v));
const list = (v: unknown): unknown[] => (Array.isArray(v) ? v : v === null || v === undefined
  ? [] : [v]);

/** One standard, as much of it as the module recorded. Kept identical to what PlanView drew. */
function Standard({ std }: { std: string | UnitStandard }) {
  if (typeof std === "string") return <li className="pv-standard"><code>{std}</code></li>;
  const where = [std.grade && `Grade ${std.grade}`, std.disciplines?.join(", ")]
    .filter(Boolean).join(" · ");
  return (
    <li className="pv-standard">
      <div className="pv-std-top">
        <code>{std.code}</code>
        {where && <span className="pv-std-where">{where}</span>}
      </div>
      {std.statement && <p className="pv-std-text">{std.statement}</p>}
      {(std.framework || std.source) && (
        <p className="pv-std-src">{[std.framework, std.source].filter(Boolean).join(" · ")}</p>
      )}
    </li>
  );
}

/** Consecutive blocks of one type, drawn as the group they are: three source references in a row
 *  are a reading list, not three unrelated lines. */
function runs(blocks: Block[]): Block[][] {
  const out: Block[][] = [];
  for (const b of blocks) {
    const last = out[out.length - 1];
    if (last && last[0].type === b.type) last.push(b);
    else out.push([b]);
  }
  return out;
}

// ---- the registry -----------------------------------------------------------------------------

export const REGISTRY: Record<string, ComponentSpec> = {
  EntityHeader: {
    name: "EntityHeader",
    whenToUse:
      "The top of a unit or lesson page. Use it once, first, to say where the thing sits, what "
      + "it is called, and the one line that identifies it — a unit's essential question, or a "
      + "lesson's summary.",
    accepts: "Short text and numbers. It composes the two joined lines itself — where the thing "
      + "sits, and the facts that identify it — because joining is a presentation decision and "
      + "the content model holds the parts.",
    props: {
      course: { type: "text", semantic: "The course this belongs to." },
      within: { type: "text", semantic: "The unit a lesson sits in. Absent on a unit." },
      title: { type: "text", required: true, semantic: "What the thing is called." },
      question: { type: "text", semantic: "A unit's essential question, set apart from the rest." },
      summary: { type: "prose", semantic: "What this is, in a sentence, when it has one." },
      gradeBand: { type: "text", semantic: "The grades it is written for." },
      discipline: { type: "text", semantic: "The subject it is taught in." },
      weeks: { type: "number", semantic: "How long it runs." },
      lessonCount: { type: "number", semantic: "How many lessons are under it, on a unit." },
    },
    render: (p) => {
      const where = [str(p.course), str(p.within)].filter(Boolean).join(" · ");
      const facts = [
        str(p.gradeBand), str(p.discipline),
        p.weeks ? `${p.weeks} weeks` : "",
        p.lessonCount ? `${p.lessonCount} lessons` : "",
      ].filter(Boolean);
      return (
        <header className="pv-head">
          {where && <p className="pv-where">{where}</p>}
          <h2>{str(p.title)}</h2>
          {str(p.question) && <p className="pv-question">{str(p.question)}</p>}
          {str(p.summary) && <p className="pv-summary">{str(p.summary)}</p>}
          {facts.length > 0 && <p className="pv-meta">{facts.join(" · ")}</p>}
        </header>
      );
    },
  },

  Page: {
    name: "Page",
    whenToUse:
      "The outermost node of a whole-page view, and only that. It carries the page's own spacing "
      + "and nothing else; use Section for a titled group inside it.",
    accepts: "Any components as children.",
    props: {},
    children: true,
    render: (_p, children) => <div className="pv">{children}</div>,
  },

  Section: {
    name: "Section",
    whenToUse:
      "A titled region of a page that groups things belonging together. Use it when a reader "
      + "would name the group — 'Lessons', 'Handout' — and not merely to put a box around "
      + "something.",
    accepts: "A heading, and any components as children.",
    props: { title: { type: "text", semantic: "The heading, in a teacher's words." } },
    children: true,
    render: (p, children) => (
      <section className="pv-section">
        {str(p.title) && <h3>{str(p.title)}</h3>}
        {children}
      </section>
    ),
  },

  Fields: {
    name: "Fields",
    whenToUse:
      "The wrapper for a run of labelled facts about the thing on screen. Use it around Fact, "
      + "TaskPrompt, StandardsList and BlockContent so their labels line up as one description "
      + "rather than as separate blocks.",
    accepts: "Any labelled component as children.",
    props: {},
    children: true,
    render: (_p, children) => <dl className="pv-fields">{children}</dl>,
  },

  Fact: {
    name: "Fact",
    whenToUse:
      "One labelled value that is short enough to read at a glance — a grade band, a discipline, "
      + "a number of weeks, a publisher's handle. Use Prose instead when the value is a sentence.",
    accepts: "Text or a number.",
    props: {
      label: { type: "text", required: true, semantic: "What the value is." },
      value: { type: "text", required: true, semantic: "The value itself." },
      mono: { type: "boolean", semantic: "The value is an identifier, not prose, and should be "
        + "set in code type." },
    },
    render: (p) => (
      <Field label={str(p.label)}>
        {p.mono ? <code>{str(p.value)}</code> : str(p.value)}
      </Field>
    ),
  },

  Prose: {
    name: "Prose",
    whenToUse:
      "A labelled paragraph of narrative — what a lesson is, what a skill asks for. Use it for "
      + "anything a teacher reads as a sentence rather than looks up as a value.",
    accepts: "One string.",
    props: {
      label: { type: "text", semantic: "What the passage is about." },
      text: { type: "prose", required: true, semantic: "The passage." },
    },
    render: (p) => (str(p.text) ? (
      str(p.label)
        ? <Field label={str(p.label)}><p>{str(p.text)}</p></Field>
        : <p className="pv-summary">{str(p.text)}</p>
    ) : null),
  },

  TaskPrompt: {
    name: "TaskPrompt",
    whenToUse:
      "The prompt students actually write to. Use it for the teaching task or a mini-task's "
      + "instruction — never for commentary about the task, which is Prose.",
    accepts: "The prompt as one string, and optionally the name of the template it was written "
      + "from.",
    props: {
      label: { type: "text", semantic: "What to call it. 'Teaching task' unless the screen has "
        + "a reason to say otherwise." },
      template: { type: "text", semantic: "The template the prompt was written from, named above "
        + "it because it says what kind of writing this is." },
      text: { type: "prose", required: true, semantic: "The prompt." },
    },
    render: (p) => (
      <Field label={str(p.label) || "Teaching task"}>
        {str(p.template) && <p className="pv-template">{str(p.template)}</p>}
        {str(p.text) && <p>{str(p.text)}</p>}
      </Field>
    ),
  },

  StandardsList: {
    name: "StandardsList",
    whenToUse:
      "What a unit or lesson claims to address. Always use this rather than printing codes: a "
      + "code on its own is a citation a teacher has to already know, and this shows the "
      + "statement the standard asks for.",
    accepts: "A list whose items are either bare code strings or objects with code, statement, "
      + "grade, disciplines and framework. Both render; described ones read better.",
    props: {
      label: { type: "text", semantic: "The heading. The count is added automatically." },
      standards: { type: "standards", required: true, semantic: "The standards as tagged." },
    },
    render: (p) => {
      const items = list(p.standards) as (string | UnitStandard)[];
      if (items.length === 0) return null;
      return (
        <Field label={`${str(p.label) || "Standards"} (${items.length})`}>
          <ul className="pv-standards">
            {items.map((s) => (
              <Standard key={typeof s === "string" ? s : s.code} std={s} />
            ))}
          </ul>
        </Field>
      );
    },
  },

  BlockContent: {
    name: "BlockContent",
    whenToUse:
      "The handout as it was written: an ordered run of typed blocks — the prompt, the sources, "
      + "an organiser, space to respond. Use it whenever content has structure worth keeping; "
      + "use Prose only for a single unstructured passage.",
    accepts: "A list of blocks, each with a `type` the content schema declares.",
    props: {
      label: { type: "text", semantic: "The heading for the whole run, when it needs one." },
      blocks: { type: "blocks", required: true, semantic: "The blocks, in order." },
    },
    render: (p) => {
      const blocks = list(p.blocks) as Block[];
      if (blocks.length === 0) return null;
      const body = runs(blocks).map((run, i) => <BlockRun key={i} run={run} />);
      return str(p.label) ? <Field label={str(p.label)}>{body}</Field> : <>{body}</>;
    },
  },

  SourceList: {
    name: "SourceList",
    whenToUse:
      "The texts a unit or lesson is built on, named and in the order they are read. Use it "
      + "whenever content lists what students read; it is not for lists in general.",
    accepts: "A list of strings, each naming one source.",
    props: {
      label: { type: "text", semantic: "The heading. The count is added automatically." },
      texts: { type: "text_list", required: true, semantic: "The sources, in reading order." },
    },
    render: (p) => {
      const items = list(p.texts).map(str).filter(Boolean);
      if (items.length === 0) return null;
      return (
        <Field label={`${str(p.label) || "Texts"} (${items.length})`}>
          {/* Named, in order. A count is not something a teacher can act on. */}
          <ol className="pv-texts">{items.map((t) => <li key={t}>{t}</li>)}</ol>
        </Field>
      );
    },
  },

  ResourceList: {
    name: "ResourceList",
    whenToUse:
      "The links a unit or lesson carries — texts, student handouts, teacher materials. Use it "
      + "wherever a teacher would go looking for the module's own documents; it groups them by "
      + "who they are for, which a flat list of links loses.",
    accepts: "A list of {title, url, kind}, where kind is text, student or teacher.",
    props: {
      label: { type: "text", semantic: "The heading. The count is added automatically." },
      resources: { type: "resources", required: true,
                   semantic: "The links, in the order the module lists them." },
    },
    render: (p) => {
      const items = list(p.resources) as ContentResource[];
      if (items.length === 0) return null;
      // The module's own three groups, in its own order. A `kind` we do not know about is shown
      // rather than dropped: an unfamiliar group is still a link somebody put there on purpose.
      const groups: [string, string][] = [
        ["text", "Texts"], ["student", "For students"], ["teacher", "For teachers"]];
      const known = new Set(groups.map(([k]) => k));
      const rest = items.filter((r) => !known.has(r.kind));
      return (
        <Field label={`${str(p.label) || "Resources"} (${items.length})`}>
          <div className="pv-res">
            {[...groups.map(([kind, title]) =>
                 [title, items.filter((r) => r.kind === kind)] as [string, ContentResource[]]),
              ["Other", rest] as [string, ContentResource[]]]
              .filter(([, group]) => group.length > 0)
              .map(([title, group]) => (
                <div key={title} className="pv-res-group">
                  <div className="pv-res-kind">{title}</div>
                  <ul>
                    {group.map((r) => (
                      <li key={r.url + r.title}>
                        {/* `noreferrer` as well as `noopener`: these are a publisher's links and
                            there is no reason to tell them which lesson a teacher came from. */}
                        <a href={r.url} target="_blank" rel="noopener noreferrer">{r.title}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
          </div>
        </Field>
      );
    },
  },

  SkillCallout: {
    name: "SkillCallout",
    whenToUse:
      "The one skill a lesson teaches, named and then written out as what a student must be able "
      + "to do. Use it near the top of a lesson, above its handout.",
    accepts: "Two short strings.",
    props: {
      skill: { type: "text", required: true, semantic: "The skill, named." },
      statement: { type: "prose", semantic: "What the skill asks a student to do." },
    },
    render: (p) => (
      <div className="pv-skill">
        <div className="pv-skill-name">{str(p.skill)}</div>
        {str(p.statement) && <p className="pv-skill-text">{str(p.statement)}</p>}
      </div>
    ),
  },

  HostSlot: {
    name: "HostSlot",
    whenToUse:
      "A marked place for something the spec may not describe: student work, cohort counts, or a "
      + "control that changes data. Use it to say WHERE such a thing goes; the screen supplies "
      + "what goes in it.",
    accepts: "Nothing. The host fills it by name.",
    props: {
      slot: { type: "text", required: true, semantic: "Which host region this is — the screen "
        + "matches on this name." },
    },
    render: () => null,   // replaced by the host's own node in render.tsx
  },
};

function BlockRun({ run }: { run: Block[] }) {
  const type = run[0].type;
  if (type === "source_reference") {
    return (
      <ol className="pv-texts">
        {run.map((b, i) => (
          <li key={i}>
            {(b as { title: string }).title}
            {(b as { citation?: string }).citation && (
              <span className="pv-cite"> · {(b as { citation?: string }).citation}</span>
            )}
          </li>
        ))}
      </ol>
    );
  }
  return (
    <>
      {run.map((b, i) => {
        switch (b.type) {
          case "task_prompt":
            return <p key={i} className="pv-prompt">{b.text}</p>;
          case "paragraph":
            return <p key={i}>{b.text}</p>;
          case "teacher_note":
            return <p key={i} className="pv-note-block">{b.text}</p>;
          case "organizer":
            return (
              <div key={i} className="pv-organizer">
                {b.title && <div className="pv-org-title">{b.title}</div>}
                {b.slots && b.slots.length > 0 && (
                  <ul className="pv-org-slots">{b.slots.map((s, j) => <li key={j}>{s}</li>)}</ul>
                )}
              </div>
            );
          case "response_area":
            return (
              <div key={i} className="pv-response">
                {b.label ?? "Student response"}
                {b.expected && <span className="pv-expected"> · {b.expected}</span>}
              </div>
            );
          case "rubric_row_ref":
            // The id, never the wording. Descriptors live in the registry and are fetched by the
            // rubric itself; a handout that carried a copy would be a second source of truth for
            // what a level says.
            return <span key={i} className="pv-rowref">{b.trait_id}</span>;
          default:
            return null;
        }
      })}
    </>
  );
}
